// A Dashboard tile opens the LIST filtered to exactly what it counts (PATTERNS.md BP-93).
//
// The two halves travel through the URL, so the filtered list is a real, shareable,
// back-button-able address, and the Filters panel shows (and can clear) the filter:
//
//   tile  <Link to={filteredListTo("status", 9)} relative="path">   dashboard → ../list?status=9
//   list  const items = useUrlFilterItems(URL_FILTERS)             ?status=9 → [{field:"status", operator:"is", value:"9"}]
//         <AeronetDataGrid key={urlFilterKey(items)} initialFilterItems={items} … />
//
// Only a field the list names in `allowed`, with a value from its list, becomes a filter:
// a hand-edited URL cannot filter by a column the list does not offer.
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { GridFilterItem } from "@mui/x-data-grid-pro";

/** The one operator a tile link can express: equality on a singleSelect column. */
export const URL_FILTER_OPERATOR = "is";

/**
 * Relative target from a sibling MODE route (`<base>/dashboard`) to the list mode, filtered.
 * Pair it with `relative="path"` on the Link: the module's mode layout is pathless, so a
 * route-relative `..` would climb past the module base.
 */
export function filteredListTo(field: string, value: string | number, listSegment = "list"): string {
  return filteredListToAll({ [field]: value }, listSegment);
}

/**
 * `filteredListTo` for a tile whose count is SEVERAL columns' values at once
 * (Due Date Control's Overdue tile: Status is Open AND Remaining is Overdue →
 * `../list?status=1&remaining=overdue`). Each field becomes one filter item on
 * the list's side, and the grid ANDs them, as the tile's count does.
 */
export function filteredListToAll(
  filters: Readonly<Record<string, string | number>>,
  listSegment = "list"
): string {
  const params = new URLSearchParams(
    Object.entries(filters).map(([field, value]) => [field, String(value)])
  );
  return `../${listSegment}?${params.toString()}`;
}

/**
 * The list's half: `?<field>=<value>` → the grid's initial filter items. `allowed` must be
 * a module-level constant (it is a memo dependency).
 */
export function useUrlFilterItems(allowed: Readonly<Record<string, readonly string[]>>): GridFilterItem[] {
  const [params] = useSearchParams();
  const search = params.toString();
  return useMemo(() => {
    const p = new URLSearchParams(search);
    return Object.entries(allowed).flatMap(([field, values]) => {
      const v = p.get(field);
      return v !== null && values.includes(v)
        ? [{ id: `url-${field}`, field, operator: URL_FILTER_OPERATOR, value: v }]
        : [];
    });
  }, [search, allowed]);
}

/**
 * A `key` for the grid: the seed is read once, on mount, so a different URL filter (back
 * button, another tile) must remount the grid rather than be ignored.
 */
export function urlFilterKey(items: GridFilterItem[]): string {
  return items.map((i) => `${i.field}=${i.value}`).join("&") || "unfiltered";
}
